import { createSelector, createSlice } from '@reduxjs/toolkit';
import {
  resetTempFilters,
  setFilters,
} from 'feature/loading-management/filters/store/filtersSlice';
import { loadingManagementApi } from 'feature/loading-management/loadingManagementApi';
import * as R from 'ramda';

// Utils
import http from 'utils/http';
import { isNotNil } from 'feature/loading-management/utils/helpers';
import { getStringifiedQueryParams } from 'utils/helpers/fetch';
import { decodeURI } from 'utils/helpers/url';
import { createSnack } from 'snackbar/snackbarSlice';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';

const initialState = {
  activeSupplierAuthTab: 'baseProducts',
  dialogDetails: {
    confirmAction: undefined,
    header: '',
    message: '',
    params: {},
  },
  filtersMenuOptions: {},
  forceCloseEditDrawer: false,
  hasUnsavedEditChanges: false,
  mobileFilterDrawerOpen: false,
  selectorsList: [],
  selectorsListLoading: true,
  supplierAuthorizationDetails: {
    active: false,
    command: {},
    dataCompany: '',
    inventoryOwner: {},
    location: {},
    product: {},
    supplier: {},
  },
  supplierAuthorizationDetailsPending: true,
  supplierAuthorizationDialogOpen: '',
  supplierAuthorizations: null,
  supplierAuthorizationsCards: [],
  supplierAuthorizationsPending: true,
};

const reducers = {
  resetSupplierAuth() {
    return initialState;
  },
  setActiveSupplierAuthTab(state, { payload }) {
    return R.assoc('activeSupplierAuthTab', payload, state);
  },
  setDialogDetails(state, { payload }) {
    return R.assoc('dialogDetails', payload, state);
  },
  setFiltersMenuOptions(state, { payload }) {
    const { name, value } = payload;
    return R.assoc(`${[name]}FiltersMenuOptions`, value, state);
  },
  setForceCloseEditDrawer(state, { payload }) {
    return R.assoc('forceCloseEditDrawer', payload, state);
  },
  setHasUnsavedEditChanges(state, { payload }) {
    return R.assoc('hasUnsavedEditChanges', payload, state);
  },
  setMobileFilterDrawerOpen(state, { payload }) {
    return R.assoc('mobileFilterDrawerOpen', payload, state);
  },
  setSelectorsList(state, { payload }) {
    return R.assoc('selectorsList', payload, state);
  },
  setSelectorsListLoading(state, { payload }) {
    return R.assoc('selectorsListLoading', payload, state);
  },
  setSingleSelector(state, { payload: updateDetails }) {
    const { nameShort } = updateDetails;
    const index = state.selectorsList.findIndex(el => el.nameShort === nameShort);
    return R.assocPath(['selectorsList', index], updateDetails, state);
  },
  setSupplierAuthorizationDetails(state, { payload }) {
    return R.assoc('supplierAuthorizationDetails', payload, state);
  },
  setSupplierAuthorizationDetailsPending(state, { payload }) {
    return R.assoc('supplierAuthorizationDetailsPending', payload, state);
  },
  setSupplierAuthorizationDialogOpen(state, { payload: selfAuthDialogName }) {
    return R.assoc('supplierAuthorizationDialogOpen', selfAuthDialogName, state);
  },
  setSupplierAuthorizations(state, { payload }) {
    return R.assoc('supplierAuthorizations', payload, state);
  },
  setSupplierAuthorizationsCards(state, { payload }) {
    return R.assoc('supplierAuthorizationsCards', payload, state);
  },
  setSupplierAuthorizationsPending(state, { payload }) {
    return R.assoc('supplierAuthorizationsPending', payload, state);
  },
  updateSupplierAuthorizations(state, { payload: updateDetails }) {
    const { active, activeSupplierAuthTab, productAuthorizationId, viewBy } = updateDetails;
    const isProduct = viewBy === 'product';
    const cardPath = isProduct
      ? ['supplierAuthorizations']
      : ['supplierAuthorizations', activeSupplierAuthTab];
    const currentCardList = R.path(cardPath, state);
    const index = currentCardList.findIndex(
      el => el.productAuthorizationId === productAuthorizationId
    );
    const cardToUpdatePath = [...cardPath, index, 'active'];
    return R.assocPath(cardToUpdatePath, !active, state);
  },
  updateTwoPartyExchange(state, { payload: updateDetails }) {
    const { activeSupplierAuthTab, exchange, productAuthorizationId, viewBy } = updateDetails;
    const isProduct = viewBy === 'product';
    const cardPath = isProduct
      ? ['supplierAuthorizations']
      : ['supplierAuthorizations', activeSupplierAuthTab];
    const currentCardList = R.path(cardPath, state);
    const index = currentCardList.findIndex(
      el => el.productAuthorizationId === productAuthorizationId
    );
    const cardToUpdatePath = [...cardPath, index, 'exchange'];
    return R.assocPath(cardToUpdatePath, exchange, state);
  },
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'supplierAuthorizations',
  reducers,
});

// Selectors

export const getSupplierAuthorizationDetails = createSelector(
  R.path(['loadingManagement', 'supplierAuthorizations', 'supplierAuthorizationDetails']),
  R.compose(R.defaultTo({}), R.identity)
);

export const {
  resetSupplierAuth,
  setActiveSupplierAuthTab,
  setDialogDetails,
  setFiltersMenuOptions,
  setForceCloseEditDrawer,
  setHasUnsavedEditChanges,
  setMobileFilterDrawerOpen,
  setSelectorsList,
  setSelectorsListLoading,
  setSingleSelector,
  setSupplierAuthorizationDialogOpen,
  setSupplierAuthorizations,
  setSupplierAuthorizationsCards,
  setSupplierAuthorizationsPending,
  setSupplierAuthorizationDetails,
  setSupplierAuthorizationDetailsPending,
  updateSupplierAuthorizations,
  updateTwoPartyExchange,
} = actions;

// Selectors
export const getSelectorsData = createSelector(
  state => state.loadingManagement.filters.searchTerm,
  state => state.loadingManagement.supplierAuthorizations.selectorsList,
  (searchTerm, selectorsList) => {
    if (searchTerm) {
      return selectorsList.filter(({ nameShort }) =>
        nameShort.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return selectorsList;
  }
);

export const getCurrentSelection = createSelector(
  getSelectorsData,
  state => state.loadingManagement.filters.selectedIndex,
  (selectorsData, selectedIndex) => selectorsData[selectedIndex] || {}
);

export const openUnsavedEditSupplierAuthDialog = () => dispatch => {
  dispatch(
    openConfirmationDialog({
      confirmAction: () => dispatch(setForceCloseEditDrawer(true)),
      header: 'Unsaved Changes',
      leftButtonActionType: 'confirm',
      leftButtonText: 'Continue',
      message:
        'Are you sure you want to leave this page? You have unsaved Supplier Authorization changes.',
      params: {},
      rightButtonActionType: 'cancel',
      rightButtonText: 'Keep Editing',
    })
  );
};

// Actions
export const toggleBatchActivation =
  (
    { productType, successMessage, supplierAuthorizationsCards, toggleType, viewBy },
    currentAffiliate
  ) =>
  async (dispatch, getState) => {
    const { customerIdName: companyCustomerIdName, dataCompanyId } = currentAffiliate;

    const { activeSupplierAuthTab, selectorsList, supplierAuthorizations } =
      getState().loadingManagement.supplierAuthorizations;

    const currentSelection = getCurrentSelection(getState());

    const {
      locationId = null,
      productId = null,
      idName = null,
      dataCompanyId: supplierDataCompanyId,
    } = currentSelection;

    const url = () => {
      const urlVariable = viewBy === 'supplier' ? '-' : `-${viewBy}-`;
      return `supplier-authorizations/${toggleType}${urlVariable}supplier-authorizations`;
    };

    const supplier = idName
      ? {
          companyCustomerIdName: idName,
          dataCompanyId: supplierDataCompanyId,
        }
      : null;

    const getAuthorizationIds = R.map(({ productAuthorizationId }) => productAuthorizationId);
    const reqBody = R.pickBy(isNotNil, {
      authorizationIds: getAuthorizationIds(supplierAuthorizationsCards),
      inventoryOwner: {
        companyCustomerIdName,
        dataCompanyId,
      },
      isFiltered: true,
      locationId,
      productId,
      productType,
      supplier,
    });

    http.post(url(), reqBody).then(({ data }) => {
      const { activatedAuthIds, deactivatedAuthIds } = data;
      const updatedList = () => {
        const idsToUpdate = activatedAuthIds ?? deactivatedAuthIds;
        const productListToUpdate =
          viewBy === 'product'
            ? supplierAuthorizations
            : supplierAuthorizations[activeSupplierAuthTab];
        const updatedValue = toggleType === 'activate';
        const updateList = R.map(item =>
          R.assoc(
            'active',
            R.includes(item.productAuthorizationId, idsToUpdate) ? updatedValue : item.active,
            item
          )
        );
        return viewBy === 'product'
          ? updateList(productListToUpdate)
          : R.assoc(activeSupplierAuthTab, updateList(productListToUpdate), supplierAuthorizations);
      };

      const getUpdatedCounts = list => {
        const listToReduce = Array.isArray(list)
          ? list
          : R.concat(list.baseProducts, list.customProducts);
        return R.reduce(
          (acc, item) => {
            item.active ? (acc.activeCount += 1) : (acc.inactiveCount += 1);
            return acc;
          },
          { activeCount: 0, inactiveCount: 0 },
          listToReduce
        );
      };

      const updatedSelectorsList = () => {
        const { activeCount, inactiveCount } = getUpdatedCounts(updatedList());

        const idx = R.findIndex(R.propEq('nameShort', currentSelection.nameShort), selectorsList);
        const updatedSelector = {
          ...currentSelection,
          activeAuthorizationsCount: activeCount,
          inactiveAuthorizationsCount: inactiveCount,
        };

        return R.assocPath([idx], updatedSelector, selectorsList);
      };
      dispatch(setSelectorsList(updatedSelectorsList()));
      dispatch(setSupplierAuthorizations(updatedList()));
      dispatch(
        createSnack({
          message: successMessage,
        })
      );
    });
  };

export const fetchUniqueSelectors =
  (queryParams, type, cancelToken) => async (dispatch, getState) => {
    const {
      tempSearchTerm,
      tempCardFilter: { filterBy, listType, nameShort, location, product },
    } = R.path(['loadingManagement', 'filters'], getState());
    dispatch(setFilters({ name: 'searchTerm', value: tempSearchTerm }));
    dispatch(setFilters({ name: 'selectedIndex', value: undefined }));
    filterBy && dispatch(setFilters({ name: filterBy, value: nameShort }));
    listType && dispatch(setActiveSupplierAuthTab(listType));
    location && dispatch(setFilters({ name: 'location', value: location }));
    product && dispatch(setFilters({ name: 'product', value: product }));
    dispatch(resetTempFilters());

    dispatch(setSupplierAuthorizationsPending(true));

    // Clear supplier auth so that there
    // is no stale data when swapping affiliates
    dispatch(setSupplierAuthorizations(null));
    dispatch(setSupplierAuthorizationsCards([]));

    dispatch(setSelectorsListLoading(true));
    dispatch(setSelectorsList([]));
    const paramString = getStringifiedQueryParams(queryParams);

    const url = `supplier-authorizations/get-unique-affiliate-${type}s-list?${paramString}`;
    // return the promise data so that we can see if
    // data was returned for the sidebar to determine
    // if the cards panel needs to render the no available
    // card or a list of cards
    http
      .get(url, { cancelToken })
      .then(({ data }) => {
        data && dispatch(setSelectorsList(data));
        return data;
      })
      .finally(() => dispatch(setSelectorsListLoading(false)));
  };

export const toggleSupplierAuthActivation = () => async (dispatch, getState) => {
  const {
    activeSupplierAuthTab,
    dialogDetails: {
      params: { productAuthorizationId, active, supplierName, locationName, productName, viewBy },
    },
  } = getState().loadingManagement.supplierAuthorizations;
  const currentSelection = getCurrentSelection(getState());

  const url = `supplier-authorizations/${
    active ? 'deactivate' : 'activate-supplier'
  }-authorization`;
  dispatch(setForceCloseEditDrawer(true));
  http.post(url, { productAuthorizationId }).then(({ data }) => {
    dispatch(updateSupplierAuthorizations({ ...data, active, activeSupplierAuthTab, viewBy }));
    const updatedCurrentSelection = () => {
      const activeNumber = active ? -1 : 1;
      const transformations = {
        activeAuthorizationsCount: R.add(activeNumber),
        inactiveAuthorizationsCount: R.add(-activeNumber),
      };
      return R.evolve(transformations, currentSelection);
    };
    dispatch(setSingleSelector(updatedCurrentSelection));
    dispatch(
      createSnack({
        message: `${decodeURI(productName)} loading for ${supplierName} has been turned ${
          active ? 'off' : 'on'
        } at ${locationName}.`,
      })
    );
  });
};

export const toggleTwoPartyExchange = () => async (dispatch, getState) => {
  const {
    activeSupplierAuthTab,
    dialogDetails: {
      params: {
        exchangeConfirmed,
        locationName,
        productAuthorizationId,
        productName,
        supplierName,
        viewBy,
      },
    },
  } = getState().loadingManagement.supplierAuthorizations;

  const url = `supplier-authorizations/${exchangeConfirmed ? 'deactivate' : 'activate'}-exchange`;
  http.post(url, { productAuthorizationId }).then(({ data }) => {
    dispatch(
      updateTwoPartyExchange({
        activeSupplierAuthTab,
        exchange: data.exchange,
        productAuthorizationId,
        viewBy,
      })
    );
    dispatch(
      createSnack({
        message: `Two Party Exchange for ${productName} loading for ${supplierName} has been turned ${
          exchangeConfirmed ? 'off' : 'on'
        } at ${locationName}.`,
      })
    );
  });
};

export const fetchSupplierAuthorizations = (queryParams, viewBy, cancelToken) => dispatch => {
  const paramString = getStringifiedQueryParams(queryParams);
  dispatch(setSupplierAuthorizations(null));
  dispatch(setSupplierAuthorizationsPending(true));
  http
    .get(`supplier-authorizations/view-${viewBy}-authorizations?${paramString}`, { cancelToken })
    .then(({ data }) => {
      data && dispatch(setSupplierAuthorizations(data));
      // this will keep the skeletons visible on an API error
      // but will also keep us from displaying "No Authorizations Available" when the cancel token is triggered
      data && dispatch(setSupplierAuthorizationsPending(false));
    })
    .catch(() => dispatch(setSupplierAuthorizationsPending(false)));
};

export const fetchSupplierAuthorizationDetails = productAuthorizationId => dispatch => {
  dispatch(setSupplierAuthorizationDetailsPending(true));
  http
    .get(`supplier-authorizations/edit?productAuthorizationId=${productAuthorizationId}`)
    .then(({ data }) => {
      dispatch(setSupplierAuthorizationDetails(data));
    })
    .finally(() => dispatch(setSupplierAuthorizationDetailsPending(false)));
};

export const editSupplierAuthorizationDetails = () => (dispatch, getState) => {
  const {
    activeSupplierAuthTab,
    dialogDetails: { params },
    supplierAuthorizations,
  } = getState().loadingManagement.supplierAuthorizations;
  const { viewBy } = params;

  return http
    .post('supplier-authorizations/edit', params)
    .then(({ data }) => {
      dispatch(loadingManagementApi.util.invalidateTags(['SupplierAuthorizationList']));
      if (supplierAuthorizations) {
        const { authorizationVolume, endDate, productAuthorizationId, type } = data.command;
        const cardPath = viewBy === 'product' ? [] : [activeSupplierAuthTab];
        const currentCardList = R.path(cardPath, supplierAuthorizations);
        const index = R.findIndex(R.propEq('productAuthorizationId', productAuthorizationId))(
          currentCardList
        );
        const cardToUpdatePath = [...cardPath, index];
        const updatedCard = {
          ...R.path(cardToUpdatePath, supplierAuthorizations),
          endDate,
          limitType: type !== 'UNLIMITED' ? type : '',
          limitVolume: authorizationVolume,
          remainingVolume: authorizationVolume,
          unlimited: type === 'UNLIMITED',
        };
        const updatedCards = R.set(
          R.lensPath(cardToUpdatePath),
          updatedCard,
          supplierAuthorizations
        );
        dispatch(setSupplierAuthorizations(updatedCards));
      }
      dispatch(setForceCloseEditDrawer(true));
      dispatch(
        createSnack({
          message: 'Your supplier authorization has been successfully updated.',
        })
      );
    })
    .catch(error => {
      if (error?.response?.data?.title) {
        dispatch(
          createSnack({
            message: error.response.data.title,
            options: { variant: 'error' },
          })
        );
      }
    });
};

export default reducer;
